import { Loader2, AlertCircle, RefreshCw, Filter, Trophy, Award, TrendingUp, Target, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, User, BookOpen, GraduationCap, BarChart3, Eye, Building2 } from 'lucide-react';
import DepartmentTreeSelect from '../selectors/DepartmentTreeSelect';
import { useState, useEffect, useCallback } from 'react';
import { ApiService } from '../../../../services/api';
import Modal from '../../../../components/ui/Modal';

interface BranchData {
    department: {
        id: string;
        name: string;
        color?: string;
    };
    successRate: number;
    totalStudents: number;
    totalHomeworks: number;
    completedHomeworks: number;
    completionRate: number;
    lessonScores: Array<{
        id: string;
        title: string;
        successRate: number;
        totalHomeworks: number;
        completedHomeworks: number;
        completionRate: number;
    }>;
}

interface BranchSuccessScoreReportData {
    parentDepartment: {
        id: string;
        name: string;
        color?: string;
    } | null;
    branches: BranchData[];
    statistics: {
        totalBranches: number;
        totalStudents: number;
        totalHomeworks: number;
        completedHomeworks: number;
        overallCompletionRate: number;
        overallSuccessRate: number;
    };
}

interface UserScore {
    user: {
        id: string;
        firstName: string;
        lastName: string;
        email?: string;
        schoolNumber?: string;
        department?: {
            id: string;
            name: string;
            color: string;
        };
        avatar?: string | null;
    };
    successRate: number;
    totalHomeworks: number;
    completedHomeworks: number;
    completionRate: number;
    lessonScores: Array<{
        id: string;
        title: string;
        successRate: number;
        totalHomeworks: number;
        completedHomeworks: number;
        completionRate: number;
    }>;
    rank?: number;
}

interface DepartmentDetailData {
    data: UserScore[];
    meta: {
        totalItems: number;
        totalPages: number;
        currentPage: number;
        perPage: number;
    };
    departmentScore: number;
    lessonScores: Array<{
        id: string;
        title: string;
        successRate: number;
        totalHomeworks: number;
        completedHomeworks: number;
        completionRate: number;
    }>;
    statistics: {
        totalStudents: number;
        totalHomeworks: number;
        completedHomeworks: number;
        overallCompletionRate: number;
    };
}

export default function BranchSuccessScoreReportTab() {
    const [selectedDepartment, setSelectedDepartment] = useState<string[]>([]);
    const [reportData, setReportData] = useState<BranchSuccessScoreReportData | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [filtered, setFiltered] = useState(false);

    const [studentDepartmentId, setStudentDepartmentId] = useState<string | null>(null);
    const [settingsLoading, setSettingsLoading] = useState(true);

    const [detailModalOpen, setDetailModalOpen] = useState(false);
    const [detailLoading, setDetailLoading] = useState(false);
    const [detailData, setDetailData] = useState<DepartmentDetailData | null>(null);
    const [detailError, setDetailError] = useState<string | null>(null);
    const [selectedBranchName, setSelectedBranchName] = useState<string>("");
    const [detailPage, setDetailPage] = useState(1);
    const [expandedLessons, setExpandedLessons] = useState<Set<string>>(new Set());

    const [userDetailModalOpen, setUserDetailModalOpen] = useState(false);
    const [userDetailLoading, setUserDetailLoading] = useState(false);
    const [userDetailData, setUserDetailData] = useState<any>(null);
    const [userDetailError, setUserDetailError] = useState<string | null>(null);

    useEffect(() => {
        const loadSettings = async () => {
            try {
                setSettingsLoading(true);

                const settings = await ApiService.getSettings();
                const educationSettings = settings?.education || settings;
                if (educationSettings?.studentDepartmentId) setStudentDepartmentId(educationSettings.studentDepartmentId);
            } catch (err) {
                console.error('Error loading settings:', err);
            } finally {
                setSettingsLoading(false);
            }
        };

        loadSettings();
    }, []);

    const loadReport = useCallback(async () => {
        if (!selectedDepartment || selectedDepartment.length === 0) return;

        setLoading(true);
        setError(null);

        try {
            const response = await ApiService.getBranchSuccessScoreReport(selectedDepartment[0]);

            setReportData(response);
        } catch (err: any) {
            console.error('Error loading branch success score report:', err);

            setError(err?.message || 'Rapor yüklenemedi');
            setReportData(null);
        } finally {
            setLoading(false);
        }
    }, [selectedDepartment]);

    useEffect(() => {
        if (filtered && selectedDepartment.length > 0) loadReport();
    }, [filtered, selectedDepartment]);

    const handleFilter = () => {
        if (selectedDepartment.length > 0) {
            setFiltered(true);
            loadReport();
        };
    };

    const handleClearFilters = () => {
        setSelectedDepartment([]);
        setReportData(null);
        setFiltered(false);
        setError(null);
    };

    const handleDepartmentChange = (value: string[]) => {
        setSelectedDepartment(value);
        setFiltered(false);
        setReportData(null);
        setError(null);
    };

    const handleRefresh = () => {
        if (selectedDepartment.length > 0) loadReport();
    };

    const handleViewBranchDetail = async (branchId: string, branchName: string) => {
        setDetailModalOpen(true);
        setDetailLoading(true);
        setDetailData(null);
        setDetailError(null);
        setSelectedBranchName(branchName);
        setDetailPage(1);
        setExpandedLessons(new Set());

        try {
            const data = await ApiService.getSuccessScoreReport(branchId, 1, 20);

            setDetailData(data);
        } catch (err: any) {
            console.error('Error loading branch detail:', err);

            setDetailError(err?.message || 'Şube detayları yüklenirken bir hata oluştu');
            setDetailData(null);
        } finally {
            setDetailLoading(false);
        }
    };

    const loadDetailPage = async (page: number) => {
        if (!detailData) return;

        setDetailLoading(true);
        try {
            const branch = reportData?.branches.find(b => b.department.name === selectedBranchName);
            if (!branch) return;

            const data = await ApiService.getSuccessScoreReport(branch.department.id, page, 20);
            setDetailData(data);
            setDetailPage(page);
        } catch (err: any) {
            console.error('Error loading detail page:', err);
        } finally {
            setDetailLoading(false);
        }
    };

    const handleViewUserDetail = async (userId: string) => {
        setUserDetailModalOpen(true);
        setUserDetailLoading(true);
        setUserDetailData(null);
        setUserDetailError(null);

        try {
            const data = await ApiService.getUserDetailScores(userId);
            if (!data || !data.user) throw new Error("API geçersiz veri döndürdü.");

            setUserDetailData(data);
        } catch (err: any) {
            console.error('Error loading user detail scores:', err);

            setUserDetailError(err?.message || 'Öğrenci detayları yüklenirken bir hata oluştu');
            setUserDetailData(null);
        } finally {
            setUserDetailLoading(false);
        }
    };

    const toggleLessonExpand = (lessonId: string) => {
        setExpandedLessons((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(lessonId)) newSet.delete(lessonId); else newSet.add(lessonId);

            return newSet;
        });
    };

    const getPerformanceLevel = (score: number) => {
        if (score >= 90) return {
            text: 'Mükemmel',
            color: 'text-yellow-600',
            bgColor: 'bg-yellow-50',
            borderColor: 'border-yellow-200',
            Icon: Trophy,
        };

        if (score >= 80) return {
            text: 'Çok İyi',
            color: 'text-green-600',
            bgColor: 'bg-green-50',
            borderColor: 'border-green-200',
            Icon: Award,
        };

        if (score >= 70) return {
            text: 'İyi',
            color: 'text-blue-600',
            bgColor: 'bg-blue-50',
            borderColor: 'border-blue-200',
            Icon: TrendingUp,
        };

        if (score >= 50) return {
            text: 'Orta',
            color: 'text-orange-600',
            bgColor: 'bg-orange-50',
            borderColor: 'border-orange-200',
            Icon: Target,
        };

        return {
            text: 'Geliştirilmeli',
            color: 'text-red-600',
            bgColor: 'bg-red-50',
            borderColor: 'border-red-200',
            Icon: AlertCircle,
        };
    };

    const getScoreColor = (score: number) => {
        if (score >= 90) return 'text-yellow-600';
        if (score >= 80) return 'text-green-600';
        if (score >= 70) return 'text-blue-600';
        if (score >= 50) return 'text-orange-600';

        return 'text-red-600';
    };

    const getScoreBgColor = (score: number) => {
        if (score >= 90) return 'bg-yellow-100';
        if (score >= 80) return 'bg-green-100';
        if (score >= 70) return 'bg-blue-100';
        if (score >= 50) return 'bg-orange-100';

        return 'bg-red-100';
    };

    const renderScoreBar = (score: number, className?: string) => {
        const bgColor = score >= 90
            ? 'bg-yellow-500'
            : score >= 80
                ? 'bg-green-500'
                : score >= 70
                    ? 'bg-blue-500'
                    : score >= 50
                        ? 'bg-orange-500'
                        : 'bg-red-500';

        return (
            <div className={`w-full bg-gray-200 rounded-full h-2 ${className}`}>
                <div
                    className={`h-2 rounded-full transition-all duration-300 ${bgColor}`}
                    style={{ width: `${Math.min(score, 100)}%` }}
                />
            </div>
        );
    };

    const isFilterReady = () => {
        return selectedDepartment.length > 0;
    };

    const renderFilterArea = () => (
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6 mb-6">
            <div className="flex items-center gap-3 mb-4">
                <Filter className="w-5 h-5 text-gray-600" />
                <h3 className="text-lg font-semibold text-gray-900">Filtreler</h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div>
                    <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                        <GraduationCap className="w-4 h-4" />
                        Üst Departman Seçimi
                    </label>
                    {settingsLoading ? (
                        <div className="border border-gray-300 rounded-lg p-3 flex items-center gap-2 text-gray-500">
                            <Loader2 className="w-4 h-4 animate-spin" />
                            <span className="text-sm">Yükleniyor...</span>
                        </div>
                    ) : (
                        <DepartmentTreeSelect
                            value={selectedDepartment}
                            onChange={handleDepartmentChange}
                            placeholder="Üst departman seçin..."
                            multiple={false}
                            rootId={studentDepartmentId}
                            hideLeaves={true}
                        />
                    )}
                </div>
            </div>

            <div className="flex items-center gap-3">
                <button
                    onClick={handleFilter}
                    disabled={!isFilterReady()}
                    className={`px-6 py-2 rounded-lg font-medium text-sm transition-colors ${isFilterReady()
                        ? "bg-blue-600 hover:bg-blue-700 text-white"
                        : "bg-gray-300 text-gray-500 cursor-not-allowed"
                        }`}
                >
                    Rapor Oluştur
                </button>
                {!isFilterReady() && (
                    <span className="text-xs text-gray-600">
                        Şube başarı puanı raporunu görüntülemek için lütfen üst departman seçiniz.
                    </span>
                )}
            </div>
        </div>
    );

    const renderStatisticsCards = () => {
        if (!reportData) return null;

        const performance = getPerformanceLevel(reportData.statistics.overallSuccessRate);
        const PerformanceIcon = performance.Icon;

        return (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <div className={`rounded-lg border ${performance.borderColor} ${performance.bgColor} p-4`}>
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-600">Genel Başarı Puanı</span>
                        <PerformanceIcon className={`w-5 h-5 ${performance.color}`} />
                    </div>
                    <div className={`text-3xl font-bold ${performance.color}`}>
                        %{reportData.statistics.overallSuccessRate}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">{performance.text}</div>
                </div>

                <div className="rounded-lg border border-gray-200 bg-white p-4">
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-600">Toplam Şube</span>
                        <Building2 className="w-5 h-5 text-purple-500" />
                    </div>
                    <div className="text-3xl font-bold text-gray-900">
                        {reportData.statistics.totalBranches}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">Alt departman</div>
                </div>

                <div className="rounded-lg border border-gray-200 bg-white p-4">
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-600">Toplam Öğrenci</span>
                        <User className="w-5 h-5 text-blue-500" />
                    </div>
                    <div className="text-3xl font-bold text-gray-900">
                        {reportData.statistics.totalStudents}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">Kayıtlı öğrenci</div>
                </div>

                <div className="rounded-lg border border-gray-200 bg-white p-4">
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-600">Tamamlanma Oranı</span>
                        <BarChart3 className="w-5 h-5 text-green-500" />
                    </div>
                    <div className="text-3xl font-bold text-gray-900">
                        %{reportData.statistics.overallCompletionRate}
                    </div>
                    {renderScoreBar(reportData.statistics.overallCompletionRate, "mt-2")}
                </div>
            </div>
        );
    };

    const renderBranchesTable = () => {
        if (!reportData || reportData.branches.length === 0) {
            return (
                <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-8 text-center">
                    <AlertCircle className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">Bu departmanın altında şube bulunamadı.</p>
                </div>
            );
        };

        return (
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200 bg-white flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                        <Building2 className="w-5 h-5 text-blue-600" />
                        Şube Başarı Puanları
                    </h3>
                </div>

                <div className="hidden sm:block overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sıra</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Şube Adı</th>
                                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Öğrenci Sayısı</th>
                                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Toplam Ödev</th>
                                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Tamamlanan</th>
                                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Tamamlanma</th>
                                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Başarı Puanı</th>
                                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">İşlemler</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {reportData.branches.map((branch, index) => {
                                const performance = getPerformanceLevel(branch.successRate);
                                const rank = index + 1;

                                return (
                                    <tr key={branch.department.id} className="hover:bg-gray-50 transition-colors">
                                        <td className="px-6 py-4">
                                            <span
                                                className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium ${rank <= 3
                                                    ? rank === 1
                                                        ? "bg-yellow-100 text-yellow-700"
                                                        : rank === 2
                                                            ? "bg-gray-100 text-gray-700"
                                                            : "bg-orange-100 text-orange-700"
                                                    : "bg-gray-50 text-gray-500"
                                                    }`}
                                            >
                                                {rank}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-3">
                                                <div
                                                    className="w-3 h-3 rounded-full"
                                                    style={{ backgroundColor: branch.department.color || '#6B7280' }}
                                                />
                                                <span className="font-medium text-gray-900">
                                                    {branch.department.name}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-center text-sm text-gray-900">
                                            {branch.totalStudents}
                                        </td>
                                        <td className="px-6 py-4 text-center text-sm text-gray-900">
                                            {branch.totalHomeworks}
                                        </td>
                                        <td className="px-6 py-4 text-center">
                                            <span className="text-sm font-medium text-blue-600">
                                                {branch.completedHomeworks}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-center">
                                            <div className="flex flex-col items-center">
                                                <span className="text-sm font-medium text-gray-900">%{branch.completionRate}</span>
                                                <div className="w-16 mt-1">
                                                    {renderScoreBar(branch.completionRate, "h-1.5")}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-center">
                                            <span
                                                className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-bold ${getScoreBgColor(
                                                    branch.successRate
                                                )} ${getScoreColor(branch.successRate)}`}
                                            >
                                                %{branch.successRate}
                                            </span>
                                            <div className={`text-xs mt-1 font-medium ${performance.color}`}>
                                                {performance.text}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-center">
                                            <button
                                                onClick={() => handleViewBranchDetail(branch.department.id, branch.department.name)}
                                                className="inline-flex items-center justify-center px-4 py-2 border border-blue-200 rounded-lg text-blue-600 bg-blue-50 hover:bg-blue-100 hover:border-blue-300 transition-all duration-200 text-sm font-medium"
                                                title="Detay Görüntüle"
                                            >
                                                <Eye className="w-4 h-4 mr-2" />
                                                Detay
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                <div className="sm:hidden divide-y divide-gray-200">
                    {reportData.branches.map((branch, index) => {
                        const performance = getPerformanceLevel(branch.successRate);
                        const rank = index + 1;

                        return (
                            <div key={branch.department.id} className="p-4 space-y-4">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium ${rank <= 3
                                            ? rank === 1
                                                ? "bg-yellow-100 text-yellow-700"
                                                : rank === 2
                                                    ? "bg-gray-100 text-gray-700"
                                                    : "bg-orange-100 text-orange-700"
                                            : "bg-gray-50 text-gray-500"
                                            }`}>
                                            {rank}
                                        </span>
                                        <div className="flex items-center gap-2">
                                            <div
                                                className="w-3 h-3 rounded-full"
                                                style={{ backgroundColor: branch.department.color || '#6B7280' }}
                                            />
                                            <span className="font-medium text-gray-900">
                                                {branch.department.name}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="flex flex-col items-end">
                                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-bold ${getScoreBgColor(branch.successRate)} ${getScoreColor(branch.successRate)}`}>
                                            %{branch.successRate}
                                        </span>
                                        <span className={`text-xs mt-1 ${performance.color}`}>{performance.text}</span>
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-3 text-sm">
                                    <div className="bg-gray-50 p-3 rounded-lg text-center">
                                        <div className="text-gray-500 text-xs mb-1">Öğrenci Sayısı</div>
                                        <div className="font-semibold text-gray-900">{branch.totalStudents}</div>
                                    </div>
                                    <div className="bg-gray-50 p-3 rounded-lg text-center">
                                        <div className="text-gray-500 text-xs mb-1">Toplam Ödev</div>
                                        <div className="font-semibold text-gray-900">{branch.totalHomeworks}</div>
                                    </div>
                                    <div className="bg-gray-50 p-3 rounded-lg text-center">
                                        <div className="text-gray-500 text-xs mb-1">Tamamlanan</div>
                                        <div className="font-semibold text-blue-600">{branch.completedHomeworks}</div>
                                    </div>
                                    <div className="bg-gray-50 p-3 rounded-lg text-center">
                                        <div className="text-gray-500 text-xs mb-1">Tamamlanma</div>
                                        <div className="bg-gray-200 rounded-full h-1.5 w-full mt-1 mb-1">
                                            <div className="bg-blue-600 h-1.5 rounded-full" style={{ width: `${branch.completionRate}%` }}></div>
                                        </div>
                                        <div className="font-semibold text-gray-900">%{branch.completionRate}</div>
                                    </div>
                                </div>

                                <button
                                    onClick={() => handleViewBranchDetail(branch.department.id, branch.department.name)}
                                    className="w-full flex items-center justify-center px-4 py-2 border border-blue-200 rounded-lg text-blue-600 bg-blue-50 hover:bg-blue-100 transition-colors text-sm font-medium"
                                >
                                    <Eye className="w-4 h-4 mr-2" />
                                    Detayları Görüntüle
                                </button>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    const renderDetailModal = () => {
        if (!detailModalOpen) return null;

        return (
            <Modal
                isOpen={detailModalOpen}
                onClose={() => setDetailModalOpen(false)}
                size="xl"
                title={`${selectedBranchName} - Şube Detay Raporu`}
            >
                {detailLoading && !detailData ? (
                    <div className="flex items-center justify-center py-12">
                        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                        <span className="ml-2 text-gray-600">Yükleniyor...</span>
                    </div>
                ) : detailError ? (
                    <div className="flex flex-col items-center justify-center py-12 text-center">
                        <AlertCircle className="w-12 h-12 text-red-300 mb-4" />
                        <p className="text-gray-500">{detailError}</p>
                    </div>
                ) : detailData ? (
                    <div className="max-h-[70vh] overflow-y-auto">

                        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-6">
                            <div className={`rounded-lg border p-4 ${getPerformanceLevel(detailData.departmentScore).bgColor}`}>
                                <p className="text-sm text-gray-600 mb-1">Şube Başarısı</p>
                                <p className={`text-2xl font-bold ${getScoreColor(detailData.departmentScore)}`}>
                                    %{detailData.departmentScore}
                                </p>
                            </div>
                            <div className="rounded-lg border border-gray-200 bg-white p-4">
                                <p className="text-sm text-gray-600 mb-1">Toplam Öğrenci</p>
                                <p className="text-2xl font-bold text-gray-900">{detailData.statistics.totalStudents}</p>
                            </div>
                            <div className="rounded-lg border border-gray-200 bg-white p-4">
                                <p className="text-sm text-gray-600 mb-1">Toplam Ödev</p>
                                <p className="text-2xl font-bold text-gray-900">{detailData.statistics.totalHomeworks}</p>
                            </div>
                            <div className="rounded-lg border border-gray-200 bg-white p-4">
                                <p className="text-sm text-gray-600 mb-1">Tamamlanma</p>
                                <p className="text-2xl font-bold text-green-600">%{detailData.statistics.overallCompletionRate}</p>
                            </div>
                        </div>

                        {detailData.lessonScores && detailData.lessonScores.length > 0 && (
                            <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
                                <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                                    <BookOpen className="w-4 h-4 text-blue-600" />
                                    Ders Bazlı Başarı Puanları
                                </h4>
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                    {detailData.lessonScores.map((lesson) => (
                                        <div key={lesson.id} className="border border-gray-200 rounded-lg p-3">
                                            <div className="flex items-center justify-between mb-2">
                                                <span className="text-sm font-medium text-gray-900 truncate flex-1 mr-2">
                                                    {lesson.title}
                                                </span>
                                                <span className={`text-sm font-bold ${getScoreColor(lesson.successRate)}`}>
                                                    %{lesson.successRate}
                                                </span>
                                            </div>
                                            {renderScoreBar(lesson.successRate, "h-1.5")}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className="border border-gray-200 rounded-lg overflow-hidden">
                            <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex items-center justify-between">
                                <h4 className="font-medium text-gray-900">Öğrenci Listesi</h4>
                                {detailData.meta.totalPages > 1 && (
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={() => loadDetailPage(detailPage - 1)}
                                            disabled={detailPage <= 1 || detailLoading}
                                            className="p-1 rounded border border-gray-200 hover:bg-gray-100 disabled:opacity-50"
                                        >
                                            <ChevronLeft className="w-4 h-4" />
                                        </button>
                                        <span className="text-sm text-gray-600">
                                            {detailPage} / {detailData.meta.totalPages}
                                        </span>
                                        <button
                                            onClick={() => loadDetailPage(detailPage + 1)}
                                            disabled={detailPage >= detailData.meta.totalPages || detailLoading}
                                            className="p-1 rounded border border-gray-200 hover:bg-gray-100 disabled:opacity-50"
                                        >
                                            <ChevronRight className="w-4 h-4" />
                                        </button>
                                    </div>
                                )}
                            </div>
                            <div className="divide-y divide-gray-100 max-h-80 overflow-y-auto">
                                {detailData.data.length === 0 ? (
                                    <div className="p-4 text-center text-gray-500">Öğrenci bulunamadı</div>
                                ) : (
                                    detailData.data.map((userScore, index) => {
                                        const performance = getPerformanceLevel(userScore.successRate);
                                        const rank = (detailPage - 1) * 20 + index + 1;

                                        return (
                                            <div key={userScore.user.id} className="p-3 hover:bg-gray-50 flex items-center justify-between gap-3">
                                                <div className="flex items-center gap-3 min-w-0 flex-1">
                                                    <span
                                                        className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-medium flex-shrink-0 ${rank <= 3
                                                            ? rank === 1
                                                                ? "bg-yellow-100 text-yellow-700"
                                                                : rank === 2
                                                                    ? "bg-gray-100 text-gray-700"
                                                                    : "bg-orange-100 text-orange-700"
                                                            : "bg-gray-50 text-gray-500"
                                                            }`}
                                                    >
                                                        {rank}
                                                    </span>
                                                    <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                                                        <User className="h-4 w-4 text-blue-600" />
                                                    </div>
                                                    <div className="min-w-0 flex-1">
                                                        <div className="text-sm font-medium text-gray-900 truncate">
                                                            {userScore.user.firstName} {userScore.user.lastName}
                                                        </div>
                                                        <div className="text-xs text-gray-500 truncate">
                                                            {userScore.completedHomeworks}/{userScore.totalHomeworks} ödev
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-2 flex-shrink-0">
                                                    <span
                                                        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-bold ${getScoreBgColor(
                                                            userScore.successRate
                                                        )} ${getScoreColor(userScore.successRate)}`}
                                                    >
                                                        %{userScore.successRate}
                                                    </span>
                                                    <button
                                                        onClick={() => handleViewUserDetail(userScore.user.id)}
                                                        className="p-1.5 rounded border border-gray-200 hover:bg-gray-100 text-gray-600 flex-shrink-0"
                                                        title="Detay Gör"
                                                    >
                                                        <Eye className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                        </div>
                    </div>
                ) : null}
            </Modal>
        );
    };

    const renderUserDetailModal = () => {
        if (!userDetailModalOpen) return null;

        return (
            <Modal
                isOpen={userDetailModalOpen}
                onClose={() => setUserDetailModalOpen(false)}
                size="lg"
                title="Öğrenci Detay Raporu"
            >
                {userDetailLoading ? (
                    <div className="flex items-center justify-center py-12">
                        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                        <span className="ml-2 text-gray-600">Yükleniyor...</span>
                    </div>
                ) : userDetailError ? (
                    <div className="flex flex-col items-center justify-center py-12 text-center">
                        <AlertCircle className="w-12 h-12 text-red-300 mb-4" />
                        <p className="text-gray-500">{userDetailError}</p>
                    </div>
                ) : userDetailData ? (
                    <div className="max-h-[70vh] overflow-y-auto">
                        <div className="flex flex-col sm:flex-row items-center gap-4 mb-6 p-4 bg-gray-50 rounded-lg">
                            <div className="h-16 w-16 rounded-full bg-blue-100 flex items-center justify-center">
                                <User className="h-8 w-8 text-blue-600" />
                            </div>
                            <div className="flex-1 text-center sm:text-left">
                                <h3 className="text-lg font-semibold text-gray-900">
                                    {userDetailData.user.firstName} {userDetailData.user.lastName}
                                </h3>
                                {userDetailData.user.schoolNumber && (
                                    <p className="text-sm text-gray-500">Okul No: {userDetailData.user.schoolNumber}</p>
                                )}
                            </div>
                            <div className="text-center sm:text-right">
                                <div className={`text-3xl font-bold ${getScoreColor(userDetailData.successRate)}`}>
                                    %{userDetailData.successRate}
                                </div>
                                <div className="text-sm text-gray-500">Genel Başarı Puanı</div>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                            <div className="text-center p-4 bg-blue-50 rounded-lg">
                                <div className="text-2xl font-bold text-blue-600">{userDetailData.totalHomeworks}</div>
                                <div className="text-xs text-gray-500">Toplam Ödev</div>
                            </div>
                            <div className="text-center p-4 bg-green-50 rounded-lg">
                                <div className="text-2xl font-bold text-green-600">{userDetailData.completedHomeworks}</div>
                                <div className="text-xs text-gray-500">Tamamlanan</div>
                            </div>
                            <div className="text-center p-4 bg-purple-50 rounded-lg">
                                <div className="text-2xl font-bold text-purple-600">%{userDetailData.completionRate}</div>
                                <div className="text-xs text-gray-500">Tamamlanma</div>
                            </div>
                        </div>

                        <div className="border border-gray-200 rounded-lg overflow-hidden">
                            <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                                <h4 className="font-medium text-gray-900">Ders Bazlı Başarı Puanları</h4>
                            </div>
                            <div className="divide-y divide-gray-200 max-h-60 overflow-y-auto">
                                {userDetailData.lessonScores.length === 0 ? (
                                    <div className="p-4 text-center text-gray-500">Henüz ders verisi bulunmamaktadır.</div>
                                ) : (
                                    userDetailData.lessonScores.map((lesson: any) => (
                                        <div key={lesson.id}>
                                            <div
                                                className="p-4 hover:bg-gray-50 cursor-pointer flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-0"
                                                onClick={() => toggleLessonExpand(lesson.id)}
                                            >
                                                <div className="flex items-center gap-3 w-full sm:w-auto">
                                                    <BookOpen className="w-5 h-5 text-blue-600 flex-shrink-0" />
                                                    <div className="min-w-0">
                                                        <div className="font-medium text-gray-900 truncate">{lesson.title}</div>
                                                        <div className="text-xs text-gray-500">
                                                            {lesson.completedHomeworks}/{lesson.totalHomeworks} tamamlandı
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-3 w-full sm:w-auto justify-between sm:justify-end">
                                                    <span className={`text-lg font-bold ${getScoreColor(lesson.successRate)}`}>
                                                        %{lesson.successRate}
                                                    </span>
                                                    <div className="w-24 h-2">{renderScoreBar(lesson.successRate)}</div>
                                                    {lesson.topics && lesson.topics.length > 0 && (
                                                        expandedLessons.has(lesson.id) ? (
                                                            <ChevronUp className="w-5 h-5 text-gray-400" />
                                                        ) : (
                                                            <ChevronDown className="w-5 h-5 text-gray-400" />
                                                        )
                                                    )}
                                                </div>
                                            </div>

                                            {expandedLessons.has(lesson.id) && lesson.topics && lesson.topics.length > 0 && (
                                                <div className="bg-gray-50 border-t border-gray-200">
                                                    {lesson.topics.map((topic: any) => (
                                                        <div
                                                            key={topic.id}
                                                            className="px-4 py-3 pl-12 flex items-center justify-between border-b border-gray-100 last:border-b-0"
                                                        >
                                                            <div>
                                                                <div className="text-sm text-gray-700">{topic.title}</div>
                                                                <div className="text-xs text-gray-500">
                                                                    {topic.completedHomeworks}/{topic.totalHomeworks} tamamlandı
                                                                </div>
                                                            </div>
                                                            <div className="flex items-center gap-2">
                                                                <span className={`text-sm font-bold ${getScoreColor(topic.successRate)}`}>
                                                                    %{topic.successRate}
                                                                </span>
                                                                <div className="w-16">{renderScoreBar(topic.successRate)}</div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    </div>
                ) : null}
            </Modal>
        );
    };

    return (
        <div className="h-full flex flex-col">
            {!filtered && renderFilterArea()}

            {filtered && (
                <div className="flex justify-between items-center mb-6">
                    <div className="flex flex-row flex-wrap gap-2">
                        <button
                            onClick={handleClearFilters}
                            className="px-3 sm:px-4 py-2 rounded-md text-xs sm:text-sm font-medium border flex-shrink-0 inline-flex items-center justify-center gap-1.5 sm:gap-2 bg-white text-gray-700 hover:bg-gray-50 border-gray-300"
                        >
                            <Filter className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                            <span>Filtrele</span>
                        </button>
                        <button
                            onClick={handleRefresh}
                            disabled={loading}
                            className="px-3 sm:px-4 py-2 rounded-md text-xs sm:text-sm font-medium border flex-shrink-0 inline-flex items-center justify-center gap-1.5 sm:gap-2 bg-white text-gray-700 hover:bg-gray-50 border-gray-300 disabled:opacity-50"
                        >
                            <RefreshCw className={`w-3.5 h-3.5 sm:w-4 sm:h-4 ${loading ? "animate-spin" : ""}`} />
                            <span>Yenile</span>
                        </button>
                    </div>
                </div>
            )}

            {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-center gap-3">
                    <AlertCircle className="w-5 h-5 text-red-500" />
                    <span className="text-red-700">{error}</span>
                </div>
            )}

            {loading && (
                <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                    <span className="ml-2 text-gray-600">Rapor yükleniyor...</span>
                </div>
            )}

            {filtered && !loading && reportData && (
                <div className="flex-1 min-h-0 overflow-y-auto">
                    {renderStatisticsCards()}
                    {renderBranchesTable()}
                </div>
            )}

            {renderDetailModal()}
            {renderUserDetailModal()}
        </div>
    );
}